import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import moment from 'moment';
import {
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ClockIcon,
  BellAlertIcon,
  MapPinIcon,
  PhoneIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import api from '../services/api';

const Dashboard = () => {
  const { user, isAdmin, isEmployee } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todaysCalls, setTodaysCalls] = useState([]);
  const [showCallsModal, setShowCallsModal] = useState(false);
  const [hasShownModal, setHasShownModal] = useState(false);
  const [heroImage, setHeroImage] = useState('https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&h=400&fit=crop&crop=center&q=80');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (!hasShownModal && isEmployee && todaysCalls.length > 0 && !loading) {
      const myCalls = todaysCalls.filter(call => 
        call.assigned_to === user?.id || call.assigned_to_name === user?.name
      );
      if (myCalls.length > 0) {
        setShowCallsModal(true);
        setHasShownModal(true);
      }
    }
  }, [todaysCalls, isEmployee, user, hasShownModal, loading]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [statsRes, todaysRes, settingsData] = await Promise.all([
        api.get('/api/dashboard/stats').catch(() => ({ data: {} })),
        api.get('/api/service-calls/today').catch(() => ({ data: { serviceCalls: [] } })),
        api.get('/api/settings').then(res => res.data.grouped || {}).catch(() => ({}))
      ]);

      setStats(statsRes.data);
      setTodaysCalls(todaysRes.data.serviceCalls || []);
      
      const appearanceSettings = settingsData.appearance || [];
      const heroSetting = appearanceSettings.find(s => s.setting_key === 'hero_image_url');
      
      if (heroSetting?.setting_value) {
        setHeroImage(heroSetting.setting_value);
      }
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openNavigation = (address) => {
    if (!address) {
      toast.warning('No address available');
      return;
    }
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
  };

  const myCallsToday = todaysCalls.filter(call => 
    call.assigned_to === user?.id || call.assigned_to_name === user?.name
  );

  if (loading) {
    return <div className="spinner"></div>;
  }

  // Hero background style - light blue gradient (45% opacity)
  const heroStyle = {
    backgroundImage: `linear-gradient(135deg, rgba(43, 127, 224, 0.45) 0%, rgba(30, 90, 160, 0.4) 50%, rgba(20, 60, 120, 0.45) 100%), url('${heroImage}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    borderRadius: '12px',
    padding: '50px 40px',
    minHeight: '220px',
    marginBottom: '32px',
    color: 'white',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  };

  return (
    <div className="dashboard">
      {/* Hero Section */}
      <div style={heroStyle}>
        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>
          Welcome back, {user?.name?.split(' ')[0]}!
        </h1>
        <p style={{ fontSize: 16, opacity: 0.95, textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
          {moment().format('dddd, MMMM D, YYYY')} • SVT Consulting Portal
        </p>
        {myCallsToday.length > 0 && (
          <button
            onClick={() => setShowCallsModal(true)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              padding: '10px 20px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.3)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              marginTop: 20,
              backdropFilter: 'blur(10px)',
              width: 'fit-content'
            }}
          >
            <BellAlertIcon style={{ width: 18, height: 18 }} />
            You have {myCallsToday.length} service {myCallsToday.length === 1 ? 'call' : 'calls'} today
          </button>
        )}
      </div>

      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, marginBottom: 32 }}>
        <div className="stat-card" style={{ background: 'white', borderRadius: 8, padding: 20, display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ width: 56, height: 56, borderRadius: 12, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ClipboardDocumentListIcon style={{ width: 28, height: 28, color: 'white' }} />
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 600 }}>{stats?.serviceCalls?.total_calls || 0}</div>
            <div style={{ fontSize: 14, color: '#4a5568' }}>Total Service Calls</div>
            <div style={{ fontSize: 12, color: '#a0aec0' }}>This month</div>
          </div>
        </div>

        <div className="stat-card" style={{ background: 'white', borderRadius: 8, padding: 20, display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ width: 56, height: 56, borderRadius: 12, background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ClockIcon style={{ width: 28, height: 28, color: 'white' }} />
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 600 }}>{stats?.serviceCalls?.pending_calls || 0}</div>
            <div style={{ fontSize: 14, color: '#4a5568' }}>Pending Calls</div>
            <div style={{ fontSize: 12, color: '#a0aec0' }}>Awaiting assignment</div>
          </div>
        </div>

        <div className="stat-card" style={{ background: 'white', borderRadius: 8, padding: 20, display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ width: 56, height: 56, borderRadius: 12, background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircleIcon style={{ width: 28, height: 28, color: 'white' }} />
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 600 }}>{stats?.serviceCalls?.completed_calls || 0}</div>
            <div style={{ fontSize: 14, color: '#4a5568' }}>Completed</div>
            <div style={{ fontSize: 12, color: '#a0aec0' }}>This month</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            {isAdmin ? "Today's Service Calls" : "All Today's Calls"}
          </h2>
          <span style={{ background: '#edf2f7', color: '#4a5568', padding: '4px 12px', borderRadius: 12, fontSize: 14 }}>
            {todaysCalls.length}
          </span>
        </div>
        <div className="card-body">
          {todaysCalls.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>
              <p>No service calls scheduled for today</p>
            </div>
          ) : (
            <div>
              {todaysCalls.map((call) => (
                <div key={call.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  padding: 16, 
                  background: call.assigned_to === user?.id ? '#fef3c7' : '#f7fafc', 
                  borderRadius: 8, 
                  marginBottom: 12,
                  border: call.assigned_to === user?.id ? '2px solid #fbbf24' : 'none'
                }}>
                  <div style={{ width: 80, fontWeight: 600 }}>
                    {call.scheduled_time || 'TBD'}
                  </div>
                  <div style={{ flex: 1, paddingLeft: 20, paddingRight: 20 }}>
                    <h3 style={{ fontWeight: 600, marginBottom: 4 }}>
                      {call.title}
                      {call.assigned_to === user?.id && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#d97706', fontWeight: 500 }}>
                          (Assigned to you)
                        </span>
                      )}
                    </h3>
                    <p style={{ color: '#4a5568', marginBottom: 2 }}>{call.client_name}</p>
                    <p style={{ color: '#718096', fontSize: 13 }}>{call.client_address}</p>
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span className={`status-badge status-${call.status}`}>
                      {call.status}
                    </span>
                    <button 
                      onClick={() => openNavigation(call.client_address)}
                      className="btn btn-secondary"
                      style={{ padding: '6px 12px', fontSize: 13 }}
                    >
                      Navigate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Today's Calls Modal for Employees */}
      {showCallsModal && myCallsToday.length > 0 && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 1000 
        }}>
          <div style={{ 
            background: 'white', 
            borderRadius: 8, 
            width: '90%', 
            maxWidth: 600,
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{ 
              padding: 20, 
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: 'white'
            }}>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>
                  Your Service Calls for Today
                </h2>
                <p style={{ color: '#718096', fontSize: 14 }}>
                  You have {myCallsToday.length} assigned {myCallsToday.length === 1 ? 'call' : 'calls'}
                </p>
              </div>
              <button
                onClick={() => setShowCallsModal(false)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer',
                  padding: 8
                }}
              >
                <XMarkIcon style={{ width: 24, height: 24, color: '#718096' }} />
              </button>
            </div>
            
            <div style={{ padding: 20 }}>
              {myCallsToday.map((call, index) => (
                <div key={call.id} style={{ 
                  padding: 16, 
                  background: '#f7fafc', 
                  borderRadius: 8, 
                  marginBottom: 16,
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                    <div>
                      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                        #{index + 1} - {call.title}
                      </h3>
                      <p style={{ 
                        fontSize: 18, 
                        fontWeight: 600, 
                        color: '#2B7FE0'
                      }}>
                        {call.scheduled_time || 'Time TBD'}
                      </p>
                    </div>
                    <span className={`priority-badge priority-${call.priority}`}>
                      {call.priority}
                    </span>
                  </div>
                  
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ fontWeight: 600, marginBottom: 4 }}>{call.client_name}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <MapPinIcon style={{ width: 16, height: 16, color: '#718096' }} />
                      <p style={{ color: '#4a5568', fontSize: 14 }}>{call.client_address}</p>
                    </div>
                    {call.client_phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <PhoneIcon style={{ width: 16, height: 16, color: '#718096' }} />
                        <a href={`tel:${call.client_phone}`} style={{ color: '#2B7FE0', fontSize: 14 }}>
                          {call.client_phone}
                        </a>
                      </div>
                    )}
                  </div>

                  {call.description && (
                    <div style={{ 
                      padding: 12, 
                      background: 'white', 
                      borderRadius: 4,
                      marginBottom: 12
                    }}>
                      <p style={{ fontSize: 13, color: '#4a5568' }}>{call.description}</p>
                    </div>
                  )}

                  <button 
                    onClick={() => openNavigation(call.client_address)}
                    style={{ 
                      background: '#2B7FE0', 
                      color: 'white', 
                      padding: '8px 16px', 
                      borderRadius: 6, 
                      border: 'none', 
                      cursor: 'pointer',
                      width: '100%',
                      fontSize: 14,
                      fontWeight: 500
                    }}
                  >
                    📍 Get Directions
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
